import React from "react";
import api from "../../Api/apiAxios";
import { Button, Table, Collapse, Spinner, Badge } from "react-bootstrap";
import { CardText, ChevronDown, ChevronRight, ClockHistory } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import HeaderContratos from "../../Components/HeaderContratos";
import Loading from "../../Components/Loading";
import { MyContext } from "../../Context/MyContext";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Paginacao } from "../../Components/Paginacao";
import styles from "./ContratosIndex.module.css";
import { TableItemName } from "../../Components/TableItemName";
import { PedidosContext } from "../../Context/PedidosContext";
import Mensagem from "../../Components/Mensagem";
import { ensureContratoDistribuidorId } from "./shared/ensureContratoDistribuidorId";

const ContratosIndex = () => {
  const navigate = useNavigate();

  const { listaGlobalContratos, setListaGlobalContratos, itensPorPagina, tipoPesquisaContratos, handleTipoPesquisaContratosChange } =
    React.useContext(MyContext);

  const { verificarECancelarContratos } = React.useContext(PedidosContext);

  const [pesquisa, setPesquisa] = React.useState("");
  const [contratos, setContratos] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [mensagem, setMensagem] = React.useState("");
  const [mensagemTipo, setMensagemTipo] = React.useState("warning");

  const [registroInicial, setRegistroInicial] = React.useState(1);
  const [registroFinal, setRegistroFinal] = React.useState(20);
  const [quantidadeRegistros, setQuantidadeRegistros] = React.useState(0);

  const [expandedId, setExpandedId] = React.useState(null);
  const [historicoByContratoId, setHistoricoByContratoId] = React.useState({});
  const [loadingHistoricoId, setLoadingHistoricoId] = React.useState(null);

  const cacheClienteByIdRef = React.useRef(new Map());
  const cacheDistribuidorByIdRef = React.useRef(new Map());
  const attemptedFixContratoIdsRef = React.useRef(new Set());

  const fixMissingDistribuidorIds = React.useCallback(
    async (lista) => {
      if (!Array.isArray(lista) || lista.length === 0) return;

      const usuarioId = Number(window.localStorage.getItem("usuarioId") ?? 0);
      const attempted = attemptedFixContratoIdsRef.current;

      const targets = lista.filter((c) => {
        const cid = Number(c?.id ?? 0);
        const clienteId = Number(c?.clienteId ?? 0);
        const distId = Number(c?.distribuidorId ?? 0);
        return cid > 0 && clienteId > 0 && distId === 0 && !attempted.has(cid);
      });

      if (targets.length === 0) return;

      let changed = false;
      const updatedList = [...lista];

      for (const c of targets) {
        const cid = Number(c.id);
        attempted.add(cid);

        try {
          const { contrato: contratoFix, updated } = await ensureContratoDistribuidorId({
            api,
            contrato: c,
            usuarioId,
            cacheClienteById: cacheClienteByIdRef.current,
            cacheDistribuidorById: cacheDistribuidorByIdRef.current,
          });

          if (updated && contratoFix) {
            const idx = updatedList.findIndex((x) => Number(x?.id ?? 0) === cid);
            if (idx >= 0) {
              updatedList[idx] = { ...updatedList[idx], ...contratoFix };
              changed = true;
            }
          }
        } catch (e) {
        }
      }

      if (changed) {
        setContratos(updatedList);
        if (typeof setListaGlobalContratos === "function") setListaGlobalContratos(updatedList);
      }
    },
    [setListaGlobalContratos]
  );

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const moneyBR = (value) => {
    const v = Number(value ?? 0);
    return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getStatusLabel = (statusAtual) => {
    if (statusAtual === 1) return { text: "Ativo", variant: "success" };
    if (statusAtual === 0) return { text: "Cancelado", variant: "danger" };
    return { text: "Baixado", variant: "secondary" };
  };

  const getTipoLabel = (tipoContrato) => {
    if (tipoContrato === 0) return { text: "Manut.", tone: styles.pillManut };
    if (tipoContrato === 1) return { text: "Locação", tone: styles.pillLocacao };
    return { text: "SaaS", tone: styles.pillSaas };
  };

  const getTipoPesquisaAuto = (texto) => {
    if (!texto) return "";
    const onlyNumbers = /^\d+$/;
    const hasLetters = /[a-zA-Z]/;
    if (onlyNumbers.test(texto)) return "ns";
    if (hasLetters.test(texto)) return "cliente";
    return "";
  };

  const calcularPaginaAtual = React.useCallback(() => {
    const pag = Math.ceil((registroInicial || 1) / (itensPorPagina || 20));
    return pag < 1 ? 1 : pag;
  }, [registroInicial, itensPorPagina]);

  const consultarContratosPaginado = React.useCallback(
    async ({ tipo = "", args = "" } = {}) => {
      const pagina = calcularPaginaAtual();

      const url =
        `/Contrato/consulta-paginada?` +
        `tipo=${encodeURIComponent(tipo || "ativos")}` +
        `&args=${encodeURIComponent(args || "*")}` +
        `&pagina=${pagina}` +
        `&itensPorPagina=${itensPorPagina}`;

      const resp = await api.get(url);
      const dados = resp.data?.dados ?? [];
      const total = resp.data?.total ?? 0;

      setContratos(dados);
      setQuantidadeRegistros(total);
      void fixMissingDistribuidorIds(dados);

      return { dados, total };
    },
    [calcularPaginaAtual, itensPorPagina, fixMissingDistribuidorIds]
  );

  React.useEffect(() => {
    async function fetchInitialData() {
      setLoading(true);
      try {
        if (!listaGlobalContratos) {
          const { dados } = await consultarContratosPaginado({ tipo: "", args: "" });
          setListaGlobalContratos(dados);

          verificarECancelarContratos();
        } else {
          setContratos(listaGlobalContratos);
          void fixMissingDistribuidorIds(listaGlobalContratos);
        }
      } catch (e) {
        console.log(e);
        setMensagemTipo("danger");
        setMensagem("Não foi possível carregar os contratos.");
      } finally {
        setLoading(false);
      }
    }

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const executarConsulta = React.useCallback(
    async (textoPesquisa) => {
      const tipoEscolhido =
        tipoPesquisaContratos && tipoPesquisaContratos !== "" ? tipoPesquisaContratos : getTipoPesquisaAuto(textoPesquisa);

      if (!textoPesquisa || textoPesquisa.trim().length === 0) {
        const tipo = tipoEscolhido || "ativos";
        const args = tipo === "" ? "" : "*";

        const { dados } = await consultarContratosPaginado({ tipo, args });
        setListaGlobalContratos(dados);
        return;
      }

      const { dados } = await consultarContratosPaginado({ tipo: tipoEscolhido, args: textoPesquisa });
      setListaGlobalContratos(dados);
    },
    [tipoPesquisaContratos, consultarContratosPaginado, setListaGlobalContratos]
  );

  async function pesquisarClick(event) {
    event.preventDefault();

    try {
      const tipoEscolhido =
        tipoPesquisaContratos && tipoPesquisaContratos !== "" ? tipoPesquisaContratos : getTipoPesquisaAuto(pesquisa);

      if (tipoEscolhido === "ns") {
        const onlyNumbers = /^\d+$/;
        if (!pesquisa || !onlyNumbers.test(pesquisa)) {
          setMensagemTipo("warning");
          setMensagem("Para pesquisar por Número de Série, digite apenas números.");
          return;
        }
      }

      if ((tipoEscolhido === "distribuidor" || tipoEscolhido === "cliente" || tipoEscolhido === "sistema") && (!pesquisa || pesquisa.trim().length === 0)) {
        setMensagemTipo("warning");
        setMensagem("Digite um termo de pesquisa para o filtro selecionado.");
        return;
      }

      setLoading(true);
      await executarConsulta(pesquisa);
      setPesquisa("");
    } catch (error) {
      console.log(error);
      setMensagemTipo("danger");
      setMensagem("Não foi possível realizar a pesquisa. Verifique os dados e tente novamente.");
      setPesquisa("");
      if (typeof handleTipoPesquisaContratosChange === "function") handleTipoPesquisaContratosChange("");
    } finally {
      setLoading(false);
    }
  }

  function viewContrato(contrato) {
    navigate(`/viewcontrato/${contrato.id}`);
  }

  const toggleExpand = async (contratoId) => {
    const nextId = expandedId === contratoId ? null : contratoId;
    setExpandedId(nextId);

    if (nextId && !historicoByContratoId[nextId]) {
      setLoadingHistoricoId(nextId);
      try {
        // NOVA ROTA conforme ContratoHistoricoController
        const resp = await api.get(`/ContratoHistorico/por-contrato/${nextId}`);
        setHistoricoByContratoId((prev) => ({ ...prev, [nextId]: resp.data || [] }));
      } catch (e) {
        console.log(e);
        setHistoricoByContratoId((prev) => ({ ...prev, [nextId]: [] }));
      } finally {
        setLoadingHistoricoId(null);
      }
    }
  };

  React.useEffect(() => {
    async function refetchOnPagination() {
      try {
        await executarConsulta(pesquisa);
      } catch (e) {
        console.log(e);
      }
    }

    refetchOnPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registroInicial, itensPorPagina]);

  if (loading) return <Loading />;

  return (
    <section className={styles.page}>
      <HeaderContratos titulo={"Contratos"} pesquisa={pesquisa} setPesquisa={setPesquisa} pesquisarClick={pesquisarClick} />
      <Mensagem mensagem={mensagem} setMensagem={setMensagem} variant={mensagemTipo} />

      <div className={styles.tableCard}>
        <div className={styles.tableCardHeader}>
          <div className={styles.tableTitle}>Listagem de contratos</div>
          <div className={styles.tableSubtitle}>Contratos principais (estado atual). Expanda para ver o histórico.</div>
        </div>

        <div className={styles.tableWrapper}>
          <Table hover responsive className={styles.tableModern}>
            <thead className={styles.theadSticky}>
              <tr>
                <th className={styles.colExpand}></th>
                <th>Revenda</th>
                <th>Cliente</th>
                <th>Sistema</th>
                <th>NS</th>
                <th>Início</th>
                <th>Últ. Atualização</th>
                <th >Valor (R$)</th>
                <th>Tipo</th>
                <th>Status</th>
                <th className={styles.colActions}>Ações</th>
              </tr>
            </thead>

            <tbody>
              {contratos.map((contrato) => {
                const isExpanded = expandedId === contrato.id;
                const status = getStatusLabel(contrato.statusAtual);
                const tipo = getTipoLabel(contrato.tipoContrato);

                return (
                  <React.Fragment key={contrato.id}>
                    <tr className={styles.rowMain}>
                      <td className={styles.colExpand}>
                        <button className={styles.expandBtn} onClick={() => toggleExpand(contrato.id)} aria-label="Expandir histórico">
                          {isExpanded ? <ChevronDown /> : <ChevronRight />}
                        </button>
                      </td>

                      <TableItemName title={contrato?.distribuidor?.nomeFantasia} />
                      <TableItemName title={contrato?.cliente?.razaoSocial} />

                      <td>
                        <Badge bg="light" text="dark" className={styles.badgeSoft}>
                          {contrato.sistema}
                        </Badge>
                      </td>

                      <td className={styles.mono}>{contrato.numeroSerie}</td>
                      <td>{formatDate(contrato.dataInicio)}</td>
                      <td>{formatDate(contrato.dataUltimaAtualizacao)}</td>

                      <td >{moneyBR(contrato.valorAtual)}</td>

                      <td>
                        <span className={`${styles.pill} ${tipo.tone}`}>{tipo.text}</span>
                      </td>

                      <td>
                        <span className={`${styles.statusPill} ${styles[`status_${status.variant}`]}`}>{status.text}</span>
                      </td>

                      <td className={styles.colActions}>
                        <OverlayTrigger overlay={<Tooltip id="btn-view-contrato">Visualizar contrato</Tooltip>}>
                          <Button onClick={() => viewContrato(contrato)} variant="outline-success" className={styles.actionBtn}>
                            <CardText size={18} />
                          </Button>
                        </OverlayTrigger>
                      </td>
                    </tr>

                    <tr className={styles.rowExpand}>
                      <td colSpan={11} className={styles.expandCell}>
                        <Collapse in={isExpanded}>
                          <div className={styles.expandContent}>
                            <div className={styles.expandHeader}>
                              <div className={styles.expandTitle}>
                                <ClockHistory className={styles.iconMuted} /> Histórico do contrato
                              </div>

                              {loadingHistoricoId === contrato.id && (
                                <div className={styles.loadingInline}>
                                  <Spinner animation="border" size="sm" /> Carregando histórico...
                                </div>
                              )}
                            </div>

                            {loadingHistoricoId !== contrato.id && (
                              <TimelineHistorico
                                itens={historicoByContratoId[contrato.id] || []}
                                formatDate={formatDate}
                                moneyBR={moneyBR}
                              />
                            )}
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}

              {(!contratos || contratos.length === 0) && (
                <tr>
                  <td colSpan={11} className={styles.emptyState}>
                    Nenhum contrato encontrado para os filtros informados.
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={11} className={styles.tfoot}>
                  <Paginacao
                    setRegistroFinal={setRegistroFinal}
                    setRegistroInicial={setRegistroInicial}
                    setRegistrosData={setContratos}
                    registroInicial={registroInicial}
                    registroFinal={registroFinal}
                    quantidadeRegistros={quantidadeRegistros}
                    itensPorPagina={itensPorPagina}
                    urlPesquisa={`/Contrato/consulta-paginada`}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </div>
    </section>
  );
};

function TimelineHistorico({ itens, formatDate, moneyBR }) {
  if (!itens || itens.length === 0) {
    return <div className={styles.timelineEmpty}>Nenhum histórico encontrado para este contrato.</div>;
  }

  return (
    <div className={styles.timeline}>
      {itens.map((h) => {
        const statusText = h.statusContrato === 1 ? "Ativo" : h.statusContrato === 0 ? "Cancelado" : "Baixado";

        return (
          <div className={styles.timelineItem} key={h.id}>
            <div className={styles.timelineDot} />

            <div className={styles.timelineBody}>
              <div className={styles.timelineTop}>
                <div className={styles.timelineWhen}>{formatDate(h.dataAtualizacao || h.dataAtivacao)}</div>
                <div className={styles.timelineBadges}>
                  <span className={styles.timelineBadge}>{statusText}</span>
                  <span className={styles.timelineBadgeMuted}>{h.sistema}</span>
                </div>
              </div>

              <div className={styles.timelineGrid}>
                <div>
                  <div className={styles.kLabel}>Valor</div>
                  <div className={styles.kValue}>R$ {moneyBR(h.valor)}</div>
                </div>

                <div>
                  <div className={styles.kLabel}>Ativação</div>
                  <div className={styles.kValue}>{formatDate(h.dataAtivacao)}</div>
                </div>

                <div>
                  <div className={styles.kLabel}>Cancelamento</div>
                  <div className={styles.kValue}>{h.dataCancelamento ? formatDate(h.dataCancelamento) : "—"}</div>
                </div>

                <div>
                  <div className={styles.kLabel}>Motivo</div>
                  <div className={styles.kValue}>{h.motivoCancelamento || "—"}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ContratosIndex;
